import React, { useEffect, useState } from 'react';
import { Close, Add, Delete } from "@mui/icons-material";
import api from '../../services/api';
import { Template } from './TemplatesPage';

interface TemplateFormProps {
  template?: Template | null;
  onClose: () => void;
  onSaved: () => void;
}

type WabaNumero = { uid_waba_numero: string; display_phone_number: string; status?: string };

// Espelha TemplateImportadoMeta do backend (metaTemplateImport.ts).
type VariavelImportadaMeta = { componente: 'cabecalho' | 'corpo'; parameter_name: string | null; chave_sugerida: string };
type TemplateImportadoMeta = {
  nome_meta: string;
  idioma: string;
  categoria: 'marketing' | 'utilidade' | 'autenticacao';
  status_meta: 'em_analise' | 'aprovado' | 'rejeitado';
  texto_cabecalho: string | null;
  texto_corpo: string;
  variaveis: VariavelImportadaMeta[];
};

const CATEGORIAS = [
  { value: 'marketing', label: 'Marketing' },
  { value: 'utilidade', label: 'Utilidade' },
  { value: 'autenticacao', label: 'Autenticação' },
];

// Só templates com status_meta "aprovado" (E ativo) aparecem pro atendente
// escolher na tela de Mensagens — reflete a aprovação real do template do
// lado da Meta, não é algo automático.
const STATUS_META_OPCOES = [
  { value: 'em_analise', label: 'Em análise (Meta ainda não aprovou)' },
  { value: 'aprovado', label: 'Aprovado (disponível pra envio)' },
  { value: 'rejeitado', label: 'Rejeitado pela Meta' },
];

// Espelha o catálogo fixo de src/pesca/constants/variaveisTemplate.ts (backend).
// Se uma variável nova for adicionada lá, precisa ser adicionada aqui também.
const VARIAVEIS_DISPONIVEIS = [
  { value: 'nome_pescador', label: 'Nome do Pescador' },
  { value: 'periodo', label: 'Período' },
];

const COMPONENTES = [
  { value: 'cabecalho', label: 'Cabeçalho' },
  { value: 'corpo', label: 'Corpo' },
];

// Espelha o catálogo fixo de src/pesca/constants/acoesBotaoTemplate.ts (backend).
// Um payload selecionado aqui que não tenha ação cadastrada lá simplesmente
// não aciona nada de especial quando o botão for clicado no WhatsApp — a
// mensagem cai no fluxo normal de LIDA/PROSA.
const ACOES_BOTAO_DISPONIVEIS = [
  { value: 'receber_relatorio_mensal', label: 'Receber relatório mensal' },
  { value: 'registrar_pescaria', label: 'Registrar pescaria' },
];

type VariavelForm = { _key: string; chave: string; componente: 'cabecalho' | 'corpo'; parameter_name: string };
type BotaoForm = { _key: string; texto: string; payload: string };

let seq = 0;
const novaVariavel = (): VariavelForm => ({ _key: `var-${seq++}`, chave: VARIAVEIS_DISPONIVEIS[0].value, componente: 'corpo', parameter_name: '' });
const novoBotao = (): BotaoForm => ({ _key: `btn-${seq++}`, texto: '', payload: ACOES_BOTAO_DISPONIVEIS[0].value });

const TemplateForm: React.FC<TemplateFormProps> = ({ template, onClose, onSaved }) => {
  const isEdicao = !!template;

  const [wabaNumeros, setWabaNumeros] = useState<WabaNumero[]>([]);
  const [uidWabaNumero, setUidWabaNumero] = useState(template?.uid_waba_numero || '');
  const [nomeMeta, setNomeMeta] = useState(template?.nome_meta || '');
  const [categoria, setCategoria] = useState(template?.categoria || 'utilidade');
  const [idioma, setIdioma] = useState(template?.idioma || 'pt_BR');
  const [textoCabecalho, setTextoCabecalho] = useState(template?.texto_cabecalho || '');
  const [textoCorpo, setTextoCorpo] = useState(template?.texto_corpo || '');
  const [statusMeta, setStatusMeta] = useState(template?.status_meta || 'em_analise');

  const [variaveis, setVariaveis] = useState<VariavelForm[]>(
    (template?.variaveis || []).map((v) => ({ _key: `var-${seq++}`, chave: v.chave, componente: v.componente, parameter_name: v.parameter_name || '' }))
  );
  const [botoes, setBotoes] = useState<BotaoForm[]>(
    (template?.botoes || []).map((b) => ({ _key: `btn-${seq++}`, texto: b.texto, payload: b.payload }))
  );

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  // Importação direto da Meta (Graph API) — elimina a classe de erro de
  // parameter_name digitado errado/desatualizado, preenchendo o formulário
  // com a definição oficial em vez de confiar 100% em digitação manual. O
  // cadastro manual continua funcionando normalmente como alternativa (ex.:
  // número sem waba_id configurado, ou API da Meta fora do ar).
  const [templatesMeta, setTemplatesMeta] = useState<TemplateImportadoMeta[] | null>(null);
  const [carregandoImportacao, setCarregandoImportacao] = useState(false);
  const [erroImportacao, setErroImportacao] = useState('');

  const importarDaMeta = () => {
    if (!uidWabaNumero) {
      setErroImportacao('Selecione o número de WhatsApp antes de importar.');
      return;
    }
    setCarregandoImportacao(true);
    setErroImportacao('');
    setTemplatesMeta(null);
    api.get('/pesca/templates/importar-meta', { params: { uid_waba_numero: uidWabaNumero } })
      .then(res => setTemplatesMeta(res.data.data || []))
      .catch(err => setErroImportacao(err?.response?.data?.message || 'Não consegui buscar os templates na Meta.'))
      .finally(() => setCarregandoImportacao(false));
  };

  const aplicarTemplateImportado = (t: TemplateImportadoMeta) => {
    setNomeMeta(t.nome_meta);
    setIdioma(t.idioma);
    setCategoria(t.categoria);
    setStatusMeta(t.status_meta);
    setTextoCabecalho(t.texto_cabecalho || '');
    setTextoCorpo(t.texto_corpo);
    setVariaveis(t.variaveis.map((v) => ({
      _key: `var-${seq++}`,
      chave: v.chave_sugerida,
      componente: v.componente,
      parameter_name: v.parameter_name || '',
    })));
    setTemplatesMeta(null);
  };

  // Busca os números WABA reais em vez de depender de um uid_waba_numero
  // fixo no código — esse UUID é gerado automaticamente e tem valor
  // DIFERENTE em cada ambiente (dev, produção) mesmo pro mesmo número real,
  // então um valor hardcoded só funciona no ambiente onde foi copiado.
  useEffect(() => {
    api.get('/pesca/waba-numeros')
      .then(res => {
        const numeros: WabaNumero[] = res.data.data || [];
        setWabaNumeros(numeros);
        // Novo template sem número escolhido ainda: pré-seleciona o primeiro
        // pra não deixar o dropdown vazio, mas o operador pode trocar.
        if (!uidWabaNumero && numeros.length > 0) {
          setUidWabaNumero(numeros[0].uid_waba_numero);
        }
      })
      .catch(() => setErro('Não consegui carregar os números de WhatsApp cadastrados.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atualizarVariavel = (key: string, campo: 'chave' | 'componente' | 'parameter_name', valor: string) => {
    setVariaveis(prev => prev.map(v => v._key === key ? { ...v, [campo]: valor } : v));
  };
  const adicionarVariavel = () => setVariaveis(prev => [...prev, novaVariavel()]);
  const removerVariavel = (key: string) => setVariaveis(prev => prev.filter(v => v._key !== key));

  const atualizarBotao = (key: string, campo: 'texto' | 'payload', valor: string) => {
    setBotoes(prev => prev.map(b => b._key === key ? { ...b, [campo]: valor } : b));
  };
  const adicionarBotao = () => setBotoes(prev => [...prev, novoBotao()]);
  const removerBotao = (key: string) => setBotoes(prev => prev.filter(b => b._key !== key));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nomeMeta.trim()) {
      setErro('Informe o nome do template (igual ao cadastrado na Meta).');
      return;
    }
    if (!textoCorpo.trim()) {
      setErro('Informe o texto do corpo.');
      return;
    }
    if (botoes.some(b => !b.texto.trim())) {
      setErro('Todo botão precisa ter um texto.');
      return;
    }
    if (!uidWabaNumero) {
      setErro('Selecione o número de WhatsApp do template.');
      return;
    }

    setSalvando(true);
    setErro('');
    try {
      const payload = {
        uid_waba_numero: uidWabaNumero,
        nome_meta: nomeMeta.trim(),
        categoria,
        idioma: idioma.trim() || 'pt_BR',
        texto_cabecalho: textoCabecalho.trim() || null,
        texto_corpo: textoCorpo.trim(),
        status_meta: statusMeta,
        variaveis: variaveis.map(({ chave, componente, parameter_name }) => ({
          chave,
          componente,
          ...(parameter_name.trim() ? { parameter_name: parameter_name.trim() } : {}),
        })),
        botoes: botoes.length ? botoes.map(({ texto, payload: p }) => ({ texto: texto.trim(), payload: p })) : null,
      };

      if (isEdicao && template) {
        await api.put(`/pesca/templates/${template.uid_whatsapp_template}`, payload);
      } else {
        await api.post('/pesca/templates', payload);
      }
      onSaved();
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar template.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)' }}>
            {isEdicao ? 'Editar Template' : 'Novo Template'}
          </h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Número de WhatsApp *</label>
            <select style={inputStyle} value={uidWabaNumero} onChange={(e) => setUidWabaNumero(e.target.value)}>
              {wabaNumeros.length === 0 && <option value="">Carregando...</option>}
              {wabaNumeros.map((n) => (
                <option key={n.uid_waba_numero} value={n.uid_waba_numero}>
                  {n.display_phone_number}{n.status && n.status !== 'ativo' ? ` (${n.status})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* ── Importar da Meta ────────────────────────────────────────
              Alternativa mais segura à digitação manual: busca a definição
              oficial do template (nome, texto, parameter_name) direto da
              Graph API. O cadastro manual abaixo continua disponível
              normalmente — essa importação só preenche os campos, não
              impede editar nada depois. */}
          <div style={{ ...fieldWrapStyle, background: '#f4faff', border: '1px solid #cfe8fb', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" onClick={importarDaMeta} disabled={carregandoImportacao} style={secondaryBtnStyle}>
                {carregandoImportacao ? 'Buscando na Meta...' : 'Importar da Meta'}
              </button>
              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                Preenche nome, texto e parameter_name automaticamente a partir do que está aprovado na Meta — evita digitar errado.
              </span>
            </div>

            {erroImportacao && <p style={{ color: '#f75a68', fontSize: '0.8rem', margin: '8px 0 0' }}>{erroImportacao}</p>}

            {templatesMeta && (
              templatesMeta.length === 0 ? (
                <p style={{ fontSize: '0.8rem', color: '#888', margin: '8px 0 0' }}>Nenhum template encontrado nesse número na Meta.</p>
              ) : (
                <div style={{ marginTop: '10px', maxHeight: '160px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px', background: '#fff' }}>
                  {templatesMeta.map((t) => (
                    <div
                      key={t.nome_meta}
                      onClick={() => aplicarTemplateImportado(t)}
                      style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '0.85rem' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f4faff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <strong>{t.nome_meta}</strong>
                      <span style={{ color: '#888' }}> — {CATEGORIAS.find(c => c.value === t.categoria)?.label || t.categoria} · {STATUS_META_OPCOES.find(s => s.value === t.status_meta)?.label || t.status_meta}</span>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fieldWrapStyle, flex: 2 }}>
              <label style={labelStyle}>Nome (igual ao cadastrado na Meta) *</label>
              <input
                style={inputStyle}
                value={nomeMeta}
                onChange={(e) => setNomeMeta(e.target.value)}
                placeholder="Ex: feedback_do_relatorio"
              />
            </div>
            <div style={{ ...fieldWrapStyle, flex: 1 }}>
              <label style={labelStyle}>Categoria *</label>
              <select style={inputStyle} value={categoria} onChange={(e) => setCategoria(e.target.value as any)}>
                {CATEGORIAS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div style={{ ...fieldWrapStyle, width: '100px' }}>
              <label style={labelStyle}>Idioma</label>
              <input style={inputStyle} value={idioma} onChange={(e) => setIdioma(e.target.value)} placeholder="pt_BR" />
            </div>
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Aprovação na Meta</label>
            <select style={inputStyle} value={statusMeta} onChange={(e) => setStatusMeta(e.target.value as any)}>
              {STATUS_META_OPCOES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Texto do Cabeçalho (opcional)</label>
            <input style={inputStyle} value={textoCabecalho} onChange={(e) => setTextoCabecalho(e.target.value)} placeholder="Ex: Olá, {{1}}!" />
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Texto do Corpo *</label>
            <textarea
              style={{ ...inputStyle, minHeight: '70px' }}
              value={textoCorpo}
              onChange={(e) => setTextoCorpo(e.target.value)}
              placeholder="Ex: Segue seu relatório de {{1}}."
            />
          </div>

          {/* ── Variáveis ────────────────────────────────────────────── */}
          <div style={{ ...fieldWrapStyle, borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '4px' }}>
            <label style={{ ...labelStyle, fontWeight: 'bold', color: '#444' }}>
              Variáveis ({'{{1}}, {{2}}...'} na ordem de cada componente)
            </label>
            <p style={{ fontSize: '0.75rem', color: '#888', margin: '2px 0 0' }}>
              Se o template usa parâmetro NOMEADO (ex: {'{{name}}'} em vez de {'{{1}}'}), preencha
              "Nome na Meta" com o nome exato cadastrado no template aprovado. Deixe em branco pra
              templates com parâmetro posicional.
            </p>
          </div>

          {variaveis.map((v) => (
            <div key={v._key} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <select
                style={{ ...inputStyle, flex: 2 }}
                value={v.chave}
                onChange={(e) => atualizarVariavel(v._key, 'chave', e.target.value)}
              >
                {VARIAVEIS_DISPONIVEIS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              <select
                style={{ ...inputStyle, flex: 1 }}
                value={v.componente}
                onChange={(e) => atualizarVariavel(v._key, 'componente', e.target.value)}
              >
                {COMPONENTES.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              <input
                style={{ ...inputStyle, flex: 1 }}
                placeholder="Nome na Meta (ex: name)"
                value={v.parameter_name}
                onChange={(e) => atualizarVariavel(v._key, 'parameter_name', e.target.value)}
              />
              <Delete
                fontSize="small"
                style={{ color: '#f75a68', cursor: 'pointer', flexShrink: 0 }}
                onClick={() => removerVariavel(v._key)}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={adicionarVariavel}
            style={{ ...secondaryBtnStyle, display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '14px' }}
          >
            <Add fontSize="small" /> Adicionar variável
          </button>

          {/* ── Botões ───────────────────────────────────────────────── */}
          <div style={{ ...fieldWrapStyle, borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '4px' }}>
            <label style={{ ...labelStyle, fontWeight: 'bold', color: '#444' }}>Botões (opcional)</label>
          </div>

          {botoes.map((b) => (
            <div key={b._key} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                placeholder="Texto do botão"
                value={b.texto}
                onChange={(e) => atualizarBotao(b._key, 'texto', e.target.value)}
              />
              <select
                style={{ ...inputStyle, flex: 1 }}
                value={b.payload}
                onChange={(e) => atualizarBotao(b._key, 'payload', e.target.value)}
              >
                {ACOES_BOTAO_DISPONIVEIS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              <Delete
                fontSize="small"
                style={{ color: '#f75a68', cursor: 'pointer', flexShrink: 0 }}
                onClick={() => removerBotao(b._key)}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={adicionarBotao}
            style={{ ...secondaryBtnStyle, display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '14px' }}
          >
            <Add fontSize="small" /> Adicionar botão
          </button>

          {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
            <button type="submit" disabled={salvando} style={primaryBtnStyle}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, overflowY: 'auto', padding: '20px 0',
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '90%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};
const fieldWrapStyle: React.CSSProperties = { marginBottom: '14px' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};

export default TemplateForm;
